import JSZip from 'jszip'
import type { FileModel } from '@/models/gallery.model'
import { getLogger } from '@/logger'

export class DownloadFetchError extends Error {
  readonly file: FileModel
  readonly status: number

  constructor(file: FileModel, status: number) {
    super(`Failed to fetch ${file.contentUrl} (${status})`)
    this.name = 'DownloadFetchError'
    this.file = file
    this.status = status
  }
}

/**
 * Fetches the given files and triggers a browser download. A single file is
 * downloaded directly; multiple files are bundled into a ZIP archive.
 *
 * @param files     - The files to include in the download.
 * @param galleryId - The gallery ID used to name the ZIP file.
 * @throws Re-throws any network or compression error after logging it.
 */
export async function downloadFiles(files: FileModel[], galleryId: string): Promise<void> {
  try {
    if (files.length === 1) {
      const file = files[0]
      triggerDownload(await fetchFileBlob(file), file.name)
      return
    }

    const zip = new JSZip()

    await Promise.all(
      files.map(async file => {
        zip.file(file.name, await fetchFileBlob(file))
      }),
    )

    triggerDownload(await zip.generateAsync({ type: 'blob' }), `gallery-${galleryId}.zip`)
  } catch (err) {
    getLogger().error('downloadFiles failed', err)
    throw err
  }
}

async function fetchFileBlob(file: FileModel): Promise<Blob> {
  const response = await fetch(file.contentUrl)
  if (!response.ok) {
    throw new DownloadFetchError(file, response.status)
  }
  return response.blob()
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.setAttribute('href', url)
  anchor.setAttribute('download', filename)
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
